import React, { useCallback, useEffect, useMemo, useState } from 'react';
import adminService from '../../services/adminService';
import { useUser } from '../../context/UserContext';
import { Btn, Card, FieldLabel, Loading } from './shell/ui';

const KEYS = {
  buyerMode: 'pro_buyer_fee_mode',
  buyerValue: 'pro_buyer_fee_value',
  providerMode: 'pro_provider_fee_mode',
  providerValue: 'pro_provider_fee_value',
};

const money = (cents) => `$${(cents / 100).toFixed(2)}`;

/**
 * MONEY › BOOKING FEES — spec/sections.md §9. Its own page and its own capability
 * (`fees`): someone who can flip a feature switch cannot move what the platform
 * charges. Two independent fees, each a percentage or a flat amount, with a worked
 * $100 example because a fee table is easy to misread. Stripe's cut is never waivable.
 */
export default function BookingFeesPanel() {
  const { can } = useUser();
  const canEdit = can('fees', 'edit');
  const [settings, setSettings] = useState(null);
  const [buyerMode, setBuyerMode] = useState('percent');
  const [buyerValue, setBuyerValue] = useState('0');
  const [providerMode, setProviderMode] = useState('percent');
  const [providerValue, setProviderValue] = useState('0');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    const r = await adminService.getFeeSettings();
    if (r.success) setSettings(r.data || []);
    else { setSettings([]); setError(r.error || 'Could not load the fees.'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!settings) return;
    const valueOf = (key, fallback) => settings.find((s) => s.key === key)?.value ?? fallback;
    setBuyerMode(valueOf(KEYS.buyerMode, 'percent'));
    setBuyerValue(valueOf(KEYS.buyerValue, '0'));
    setProviderMode(valueOf(KEYS.providerMode, 'percent'));
    setProviderValue(valueOf(KEYS.providerValue, '0'));
  }, [settings]);

  // Mirrors ProFeeService.ComputeAsync — a preview only; the server recomputes what it charges.
  const preview = useMemo(() => {
    const amount = 10000;
    const fee = (mode, raw) => {
      const v = parseFloat(raw);
      if (!Number.isFinite(v) || v <= 0) return 0;
      return mode === 'flat' ? Math.round(v) : Math.round((amount * v) / 100);
    };
    const buyerFee = fee(buyerMode, buyerValue);
    const providerFee = Math.min(fee(providerMode, providerValue), amount);
    const subtotal = amount + buyerFee;
    const stripe = Math.ceil((subtotal + 30) / (1 - 0.029)) - subtotal;
    return { buyerPays: subtotal, stripe, proGets: amount - providerFee, platform: buyerFee + providerFee };
  }, [buyerMode, buyerValue, providerMode, providerValue]);

  const save = async () => {
    setSaving(true); setError(''); setSaved(false);
    const writes = [
      [KEYS.buyerMode, buyerMode],
      [KEYS.buyerValue, String(buyerValue || '0')],
      [KEYS.providerMode, providerMode],
      [KEYS.providerValue, String(providerValue || '0')],
    ];
    for (const [key, value] of writes) {
      // eslint-disable-next-line no-await-in-loop
      const r = await adminService.updateFeeSetting(key, value);
      if (!r.success) { setError(r.error || `Could not save ${key}.`); setSaving(false); return; }
    }
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
    load();
  };

  const feeCard = (label, mode, setMode, value, setValue, note) => (
    <div className="rounded-[20px] bg-[#F7F7F7] p-4">
      <FieldLabel className="mb-[10px]">{label}</FieldLabel>
      {canEdit ? (
        <>
          <div className="flex w-max gap-[2px] rounded-full bg-white p-[3px]">
            {[{ v: 'percent', l: '%' }, { v: 'flat', l: '$' }].map((opt, i) => (
              <button
                key={opt.v}
                type="button"
                onClick={() => setMode(opt.v)}
                className={`h-9 rounded-full px-[14px] font-mono text-[12px] font-bold transition-colors ${mode === opt.v ? 'bg-black text-white' : 'text-black hover:bg-[#F2F2F2]'}`}
              >
                {opt.l}
              </button>
            ))}
          </div>
          <div className="mt-[10px] flex items-center gap-2">
            <input
              type="number"
              min="0"
              step={mode === 'percent' ? '0.1' : '1'}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              aria-label={label}
              className="h-10 w-28 rounded-full border-2 border-transparent bg-white px-4 font-mono text-[13px] font-bold focus:border-black focus:outline-none focus:ring-0"
            />
            <span className="font-mono text-[11px] text-[#6B7280]">{note} · {mode === 'percent' ? '% of the booking' : 'cents'}</span>
          </div>
        </>
      ) : (
        <div className="font-mono text-[13px] font-bold">
          {mode === 'percent' ? `${value || 0}%` : `$${((parseFloat(value) || 0) / 100).toFixed(2)}`}
          <span className="ml-2 font-normal text-[11px] text-[#6B7280]">{note}</span>
        </div>
      )}
    </div>
  );

  if (!settings) return <Loading />;

  return (
    <div className="flex flex-col gap-5">
      <Card title="BOOKING FEES" bodyClass="flex flex-col gap-[14px] p-[14px]">
        <div className="grid gap-[14px]" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
          {feeCard('BUYER FEE', buyerMode, setBuyerMode, buyerValue, setBuyerValue, 'added on top')}
          {feeCard('PROVIDER FEE', providerMode, setProviderMode, providerValue, setProviderValue, 'held back')}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-[14px] border-t border-[#F2F2F2] pt-4">
          <p className="font-mono text-[11px] text-[#6B7280]">
            On a $100 booking: buyer pays {money(preview.buyerPays)} plus {money(preview.stripe)} Stripe processing · provider receives {money(preview.proGets)} · Portal keeps {money(preview.platform)}.
          </p>
          {canEdit && (
            <div className="flex items-center gap-3">
              {saved && <span className="font-mono text-[10px] font-bold uppercase tracking-[0.1em]">SAVED.</span>}
              <Btn variant="solid" onClick={save} disabled={saving} className="px-[14px]">{saving ? 'SAVING…' : 'SAVE FEES'}</Btn>
            </div>
          )}
        </div>
        {error && <p className="font-mono text-[11px] text-red-600">{error}</p>}
      </Card>
      <p className="font-mono text-[11px] leading-[1.6] text-[#6B7280]">
        Feature switches are not here. They sit in SYSTEM → SETTINGS on their own capability.
      </p>
    </div>
  );
}
